import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { refreshToken } from "../views/Auth/SignIn/refreshToken";

const baseQuery = fetchBaseQuery({
  baseUrl: "",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

function hasOriginalStatus(
  error: FetchBaseQueryError
): error is FetchBaseQueryError & { originalStatus: number } {
  return (error as any).originalStatus !== undefined;
}

const customFetchBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (
    result.error &&
    hasOriginalStatus(result.error) &&
    (result.error.originalStatus === 401 || result.error.originalStatus === 500)
  ) {
    const newToken = await refreshToken();

    if (newToken) {
      // Normalize args to FetchArgs so we can set headers reliably
      let normalizedArgs: FetchArgs;
      if (typeof args === "string") {
        normalizedArgs = { url: args, method: "GET" } as FetchArgs;
      } else {
        normalizedArgs = { ...args } as FetchArgs;
      }

      const headers = normalizedArgs.headers;
      if (headers instanceof Headers) {
        headers.set("Authorization", `Bearer ${newToken}`);
        normalizedArgs.headers = headers;
      } else if (Array.isArray(headers)) {
        normalizedArgs.headers = [
          ...headers.filter(([key]) => key.toLowerCase() !== "authorization"),
          ["Authorization", `Bearer ${newToken}`],
        ];
      } else {
        normalizedArgs.headers = {
          ...(headers || {}),
          Authorization: `Bearer ${newToken}`,
        } as Record<string, string>;
      }

      result = await baseQuery(normalizedArgs, api, extraOptions);
    } else {
      window.location.href = "/sign-in";
      localStorage.clear();
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: customFetchBaseQuery,
  // Extend tags to support roles/permissions caching and invalidation
  tagTypes: [
    "Restaurant",
    "Order",
    "Menus",
    "Cart",
    "Admin",
    "Features",
    "FeatureGroups",
    "Roles",
    "PermissionMatrix",
  ],
  // Keep cached data in store for 5 minutes after last subscriber unsubscribes
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: (builder) => ({}),
});
